import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import { createStorage } from "../../storage/StorageClass";
import { useStorageClient } from "../../storage/StorageClientProvider";
import CounterMetricHandler from "./CounterMetricHandler";
import TimeMetricHandler from "./TimeMetricHandler";

const FRONTEND_SHELL_METRICS_SCOPE = "fe-shell:metric";
const DISABLE_GRAFANA_KEY = "disableGrafana";

const isTouchable = () => "ontouchstart" in window || navigator.maxTouchPoints;

const useMetricStorage = () => {
  const storageClient = useStorageClient();
  return useMemo(() => {
    const storage = createStorage(
      storageClient.get("sessionStorage"),
      FRONTEND_SHELL_METRICS_SCOPE,
    );
    const disableGrafana = () => storage.set(DISABLE_GRAFANA_KEY, true);
    return {
      clear: () => storage.clear(),
      disableGrafana,
      enableGrafana: (event) => {
        window.removeEventListener(event, disableGrafana);
        storage.remove(DISABLE_GRAFANA_KEY);
      },
    };
  }, [storageClient]);
};

const TrackingMetricsProvider = ({ children }) => {
  const metricStorage = useMetricStorage();
  useEffect(() => {
    // when iphone screen is minimized the totality of navigator processes are suspended bringing wrong metrics
    if (isTouchable()) {
      window.addEventListener("blur", metricStorage.disableGrafana);
    }
    return () => metricStorage.enableGrafana("blur");
  }, [metricStorage]);

  useEffect(() => {
    window.addEventListener("offline", metricStorage.disableGrafana);
    return () => metricStorage.enableGrafana("offline");
  }, [metricStorage]);

  useEffect(() => () => metricStorage.clear(), [metricStorage]);

  return <>{children}</>;
};

export const createTimeMetricHooks = (metricKey) => {
  const useMetric = () => {
    const storageClient = useStorageClient();
    return useMemo(
      () =>
        TimeMetricHandler(
          storageClient.get("sessionStorage"),
          metricKey,
          FRONTEND_SHELL_METRICS_SCOPE,
        ),
      [storageClient],
    );
  };
  return {
    useMetric,
    useEndMetricEffect: () => {
      const timeMetricHandler = useMetric();
      // eslint-disable-next-line react-hooks/exhaustive-deps -- intended behaviour
      useEffect(timeMetricHandler.end);
    },
  };
};

export const createCounterMetricHook = (metricKey) => {
  return () => {
    const storageClient = useStorageClient();
    return useMemo(
      () =>
        CounterMetricHandler(
          storageClient.get("sessionStorage"),
          metricKey,
          FRONTEND_SHELL_METRICS_SCOPE,
        ),
      [storageClient],
    );
  };
};
TrackingMetricsProvider.propTypes = {
  children: PropTypes.node,
};

export default TrackingMetricsProvider;
